import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth, LoginScreen, ProtectedRoute, PasswordChangeScreen, usePasswordChangeHandler } from './modules/auth';

import { AdminLayout } from './modules/layouts';
import { UsersScreen, UsersImportScreen } from './modules/users';
import { BranchOfficeScreen } from './modules/branch-office';
import { IndicadoresScreen } from './modules/indicadores';
import { SaludErpScreen } from './modules/erp';
import { VendedoresScreen } from './modules/vendedores';
import { MetasColaboradorScreen } from './modules/metas-colaborador';
import { AuthService } from './modules/auth/services/auth.service';
import { RoleService } from './modules/layouts/services/role.service';
import { setPasswordChangeRequiredHandler, setupFetchInterceptor, setGlobalPasswordChangeHandler, setGlobalNavigateHandler } from './modules/common';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './App.css';



function AppContent() {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth(navigate);
  const { handleApiError } = usePasswordChangeHandler();

  // Configurar los handlers globales para PASSWORD_CHANGE_REQUIRED
  useEffect(() => {
    // Para ApiClient
    setPasswordChangeRequiredHandler(handleApiError);
    
    // Para fetch interceptor (servicios existentes)
    setGlobalPasswordChangeHandler(handleApiError);
    
    // Configurar el handler de navegación global
    setGlobalNavigateHandler(navigate);
    
    // Configurar el interceptor de fetch
    setupFetchInterceptor();

    // Función de testing (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
      (window as any).testPasswordModal = () => {
        const mockError = {
          statusCode: 403,
          error: 'PASSWORD_CHANGE_REQUIRED',
          message: 'Test: Password change required'
        };
        handleApiError(mockError);
      };
      
      console.log('🔧 Test function available: window.testPasswordModal()');
    }
  }, [handleApiError, navigate]);

  useEffect(() => {
    const validateAuth = async () => {
      if (isAuthenticated) {
        try {
          const isValid = await AuthService.validateAndRefreshAuth();
          if (!isValid) {
            logout();
          }
        } catch (error: any) {
          // Manejar error de PASSWORD_CHANGE_REQUIRED
          if (!handleApiError(error)) {
            logout();
          }
        }
      }
    };
    
    let interval: NodeJS.Timeout;
    if (isAuthenticated) {
      interval = setInterval(validateAuth, 10 * 60 * 1000); // 10 minutes
      validateAuth(); // Validate immediately on load
    }
    
    return function cleanup() {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAuthenticated, logout, handleApiError]);

  // Ruta de aterrizaje según rol: los roles que NO administran usuarios (p.ej.
  // INDICADORES, solo lectura del tablero) van a /indicadores en vez de /users
  // (que su rol no puede consultar — 403). Ver RoleService.getLandingRoute.
  const landingRoute = RoleService.getLandingRoute();

  return (
    <>
      <Routes>
        <Route path="/auth/login" element={!isAuthenticated ? <LoginScreen /> : <Navigate to={landingRoute} replace />} />
        <Route path="/auth/change-password" element={<PasswordChangeScreen />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <AdminLayout>
              <Routes>
                <Route path="/users" element={<UsersScreen />} />
                <Route path="/users/import" element={<UsersImportScreen />} />
                <Route path="/branch-offices" element={<BranchOfficeScreen />} />
                <Route path="/indicadores" element={<IndicadoresScreen />} />
                <Route path="/erp" element={<SaludErpScreen />} />
                <Route path="/vendedores" element={<VendedoresScreen />} />
                {/* Una sola ruta para todo el módulo: la pestaña sale de /metas/<pestaña>
                    y la pantalla se monta UNA vez (cambiar de pestaña no la desmonta). */}
                <Route path="/metas/*" element={<MetasColaboradorScreen />} />
                <Route path="/" element={<Navigate to={landingRoute} replace />} />
                <Route path="*" element={<Navigate to={landingRoute} replace />} />
              </Routes>
            </AdminLayout>
          </ProtectedRoute>
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
